import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConnectionListQuery,
  ParentConnectionDecisionInput,
  RequestParentConnectionInput,
  SetTrustedConnectionInput,
} from '@teentack/shared';
import { jobKeys } from '@/features/jobs/use-jobs';
import { consentKeys } from '@/features/consent/use-consent';
import { parentKeys } from '@/features/parent/use-parent-overview';
import {
  decideParentConnection,
  fetchParentConnections,
  fetchTrustedConnections,
  removeTrustedConnection,
  requestParentConnection,
  setTrustedConnection,
} from './connections.api';

export const connectionKeys = {
  all: ['connections'] as const,
  trusted: (query: Partial<ConnectionListQuery>) => ['connections', 'trusted', query] as const,
  parents: (query: Partial<ConnectionListQuery>) => ['connections', 'parents', query] as const,
};

/**
 * Changing the graph changes what a teen is told about an employer, so job
 * lists and the consent queue both go stale — and so does the console's counts.
 */
function invalidateGraph(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: connectionKeys.all });
  void queryClient.invalidateQueries({ queryKey: jobKeys.all });
  void queryClient.invalidateQueries({ queryKey: consentKeys.all });
  void queryClient.invalidateQueries({ queryKey: parentKeys.overview });
}

export function useTrustedConnections(query: Partial<ConnectionListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: connectionKeys.trusted(query),
    queryFn: () => fetchTrustedConnections(query),
    enabled,
  });
}

export function useParentConnections(query: Partial<ConnectionListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: connectionKeys.parents(query),
    queryFn: () => fetchParentConnections(query),
    enabled,
  });
}

export function useSetTrusted() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetTrustedConnectionInput) => setTrustedConnection(input),
    onSuccess: () => invalidateGraph(queryClient),
  });
}

export function useRemoveTrusted() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobPosterId: string) => removeTrustedConnection(jobPosterId),
    onSuccess: () => invalidateGraph(queryClient),
  });
}

export function useRequestParentConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestParentConnectionInput) => requestParentConnection(input),
    onSuccess: () => invalidateGraph(queryClient),
  });
}

export function useDecideParentConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ParentConnectionDecisionInput & { connectionId: string }) =>
      decideParentConnection(input.connectionId, { decision: input.decision }),
    onSuccess: () => invalidateGraph(queryClient),
  });
}
